import { useEffect, useRef, useState } from "react";
import { useSession } from "@/auth/session";
import {
  createInstallmentAgreement,
  listInstallmentsByContract,
  updateInstallmentStatus,
  type InstallmentItem,
} from "@/lib/api";

interface Props {
  contractId: string;
  contractNumber: string;
  deudaTotal?: number;
  onClose: () => void;
  onChanged?: () => void;
}

type View = "list" | "new";

const STATUS_LABEL: Record<string, string> = {
  active: "Vigente",
  completed: "Cumplido",
  cancelled: "Anulado",
  paused: "Pausado",
};

const STATUS_CLASS: Record<string, string> = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  completed: "bg-blue-50 text-blue-700 border-blue-200",
  cancelled: "bg-gray-100 text-gray-500 border-gray-200",
  paused: "bg-amber-50 text-amber-700 border-amber-200",
};

const MODAL_TYPE = "compromiso";

function currentPeriod(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
}

export default function CompromisoModal({ contractId, contractNumber, deudaTotal = 0, onClose, onChanged }: Props) {
  const { session } = useSession();
  const [view, setView] = useState<View>("list");
  const [items, setItems] = useState<InstallmentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const submittingRef = useRef(false);

  const [form, setForm] = useState({
    amount: deudaTotal > 0 ? String(deudaTotal) : "",
    promisedPeriod: currentPeriod(),
    notes: "",
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!session) return;
    void load();
  }, [session?.userId]);

  async function load() {
    setLoading(true);
    setError("");
    try {
      const data = await listInstallmentsByContract(session!, contractId, MODAL_TYPE);
      setItems(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al cargar compromisos");
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    if (!session || submittingRef.current) return;
    const amount = Number(form.amount.replace(/\./g, "").replace(",", "."));
    if (!amount || amount <= 0) { setError("Ingresa un monto válido"); return; }
    if (!form.promisedPeriod.match(/^\d{4}-\d{2}$/)) { setError("Período inválido (formato YYYY-MM)"); return; }

    submittingRef.current = true;
    setSaving(true);
    setError("");
    try {
      await createInstallmentAgreement(session, {
        contractId,
        type: MODAL_TYPE,
        totalAmount: amount,
        installments: 1,
        startPeriod: form.promisedPeriod,
        notes: form.notes || undefined,
      });
      await load();
      onChanged?.();
      setForm({ amount: "", promisedPeriod: currentPeriod(), notes: "" });
      setView("list");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al crear compromiso");
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  }

  async function handleCancel(id: string) {
    if (!session || !confirm("¿Anular este compromiso?")) return;
    setCancellingId(id);
    try {
      await updateInstallmentStatus(session, id, "cancelled");
      await load();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al anular");
    } finally {
      setCancellingId(null);
    }
  }

  async function handleComplete(id: string) {
    if (!session || !confirm("¿Marcar este compromiso como cumplido?")) return;
    setCancellingId(id);
    try {
      await updateInstallmentStatus(session, id, "completed");
      await load();
      onChanged?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al actualizar");
    } finally {
      setCancellingId(null);
    }
  }

  const set = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((prev) => ({ ...prev, [field]: e.target.value }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-lg shadow-2xl flex flex-col max-h-[90vh]">

        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h2 className="text-base font-semibold text-gray-800">
              {view === "list" ? "Compromisos de pago" : "Nuevo compromiso"}
            </h2>
            <p className="text-xs text-gray-500 mt-0.5">Contrato {contractNumber}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {error ? (
            <div className="mb-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          ) : null}

          {view === "list" && (
            <div className="space-y-3">
              {loading ? (
                <div className="space-y-2">
                  {[1, 2].map((i) => <div key={i} className="h-14 animate-pulse rounded-xl bg-gray-100" />)}
                </div>
              ) : items.length === 0 ? (
                <div className="py-10 text-center">
                  <i className="ri-shake-hands-line text-3xl text-gray-300" />
                  <p className="mt-2 text-sm text-gray-500">Sin compromisos registrados.</p>
                </div>
              ) : (
                items.map((item) => (
                  <div key={item.id} className={`rounded-xl border p-4 ${item.status === "cancelled" ? "opacity-60" : ""}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-gray-800">
                            ${Number(item.totalAmount).toLocaleString("es-CL")}
                          </span>
                          <span className={`text-[11px] font-medium px-2 py-0.5 rounded-full border ${STATUS_CLASS[item.status] ?? STATUS_CLASS.active}`}>
                            {STATUS_LABEL[item.status] ?? item.status}
                          </span>
                          <span className="text-xs text-gray-500">para {item.startPeriod}</span>
                        </div>
                        {item.notes ? <p className="mt-1 text-xs text-gray-400 italic">{item.notes}</p> : null}
                        <p className="mt-1 text-[10px] text-gray-300">
                          Registrado {new Date(item.createdAt).toLocaleDateString("es-CL")}
                        </p>
                      </div>
                      {item.status === "active" ? (
                        <div className="flex shrink-0 flex-col gap-1">
                          <button
                            onClick={() => handleComplete(item.id)}
                            disabled={cancellingId === item.id}
                            className="text-xs px-2 py-1 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 disabled:opacity-50"
                          >
                            Cumplido
                          </button>
                          <button
                            onClick={() => handleCancel(item.id)}
                            disabled={cancellingId === item.id}
                            className="text-xs px-2 py-1 rounded-lg bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
                          >
                            {cancellingId === item.id ? "..." : "Anular"}
                          </button>
                        </div>
                      ) : null}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          {view === "new" && (
            <div className="space-y-4">
              <div className="rounded-lg bg-blue-50 border border-blue-100 px-4 py-3 text-xs text-blue-700">
                Registra una promesa de pago del cliente para un período específico.
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Monto comprometido ($)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  value={form.amount}
                  onChange={set("amount")}
                  placeholder="Ej: 25000"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none"
                  required
                />
                {deudaTotal > 0 ? (
                  <button
                    type="button"
                    onClick={() => setForm((p) => ({ ...p, amount: String(deudaTotal) }))}
                    className="mt-1 text-xs text-blue-600 hover:underline"
                  >
                    Usar deuda total: ${deudaTotal.toLocaleString("es-CL")}
                  </button>
                ) : null}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Período de pago (YYYY-MM)</label>
                <input
                  type="text"
                  value={form.promisedPeriod}
                  onChange={set("promisedPeriod")}
                  placeholder="2026-07"
                  pattern="\d{4}-\d{2}"
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none"
                  required
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Observaciones (opcional)</label>
                <textarea
                  value={form.notes}
                  onChange={set("notes")}
                  rows={2}
                  className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-blue-400 focus:outline-none resize-none"
                  placeholder="Ej: Cliente se comprometió a pagar el día 10 por ventanilla"
                />
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-6 py-4">
          {view === "list" ? (
            <>
              <button onClick={onClose} className="text-sm text-gray-500 hover:text-gray-700">
                Cerrar
              </button>
              <button
                onClick={() => { setError(""); setView("new"); }}
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
              >
                <i className="ri-add-line" /> Nuevo compromiso
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => { setError(""); setView("list"); }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                ← Volver
              </button>
              <button
                type="button"
                disabled={saving || submittingRef.current}
                onClick={() => void handleCreate()}
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {saving ? <><i className="ri-loader-4-line animate-spin" /> Guardando...</> : <><i className="ri-save-line" /> Crear compromiso</>}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
